/**
 * LIVE UPDATES BY SUBSCRIPTION, NOT BY POLLING.
 *
 * Anthony: "switch comms and everything really to subscribe over polling."
 *
 * A screen that has to stay fresh — the comms panel, the front-desk feed,
 * the leads list — used to re-read its whole table every few seconds on a
 * timer. That is a message you see up to twelve seconds late and a query
 * every desk fires whether or not anything changed. This subscribes to the
 * table instead, so a new row arrives the instant it lands and an idle
 * screen asks the database nothing.
 *
 * ── RLS STILL SCOPES IT ────────────────────────────────────────────────
 * The channel authenticates with the signed-in user's own JWT (the same
 * client `db()` hands out), so Supabase runs each change through that user's
 * SELECT policy before delivering it. A shop hears only its own rows without
 * this file ever naming an account id — the fence is the same one the reads
 * already pass.
 *
 * ── WHY THERE IS STILL A TIMER, AND WHY IT IS SAFE ─────────────────────
 * Two things can leave a pure subscription silently stale: a table that was
 * never added to the `supabase_realtime` publication (the socket subscribes
 * fine and then delivers nothing), and a dropped connection that reconnects
 * having missed what happened in the gap. So a reconcile poll stays — but it
 * TUNES ITSELF to the truth: it runs at the old fast rate until a real
 * subscription event proves the socket is delivering, and only then slows
 * right down to an occasional safety sweep. Before the enabling migration
 * lands, this behaves exactly like the poll it replaces; after it, the poll
 * all but disappears and the socket does the work.
 */

import { db } from "./client.ts";
import type { RealtimeChannel } from "@supabase/supabase-js";

export type LiveOpts = {
  /** A channel name unique to this screen, e.g. "comms". */
  channel: string;
  /** The table to watch. RLS on it is what scopes the rows to this shop. */
  table: string;
  /** Which change to hear. New messages/leads are INSERTs; default "*". */
  event?: "INSERT" | "UPDATE" | "DELETE" | "*";
  /** The screen's own refetch-and-reconcile. Called on every event and sweep. */
  refresh: () => void | Promise<void>;
  /** True while the screen is still on the page. When it goes false, this stops. */
  alive: () => boolean;
  /** Sweep interval once the socket is proven live. Default 60s. */
  slowMs?: number;
  /** Sweep interval while the socket has never delivered. Default 12s. */
  fastMs?: number;
};

/** Start listening. Returns a stop() that removes the channel and the timer. */
export function startLive(opts: LiveOpts): () => void {
  const slow = opts.slowMs ?? 60_000;
  const fast = opts.fastMs ?? 12_000;
  let lastEventAt = 0;   // when the socket last delivered — 0 = never
  /**
   * THE CLOCK STARTS NOW, NOT AT ZERO.
   *
   * It used to start at 0, which reads as "last swept in 1970" — so the
   * very first tick, five seconds after mounting, always found the gap
   * exceeded and swept immediately. On a screen that re-subscribes as part
   * of its own refresh (the leads list did) that is not a one-off cost, it
   * is a loop: refresh, re-subscribe, clock back to zero, sweep again five
   * seconds later, forever, with the configured interval never once
   * reaching. The leads screen redrew every five seconds all day.
   *
   * A caller starts this immediately after painting from a fresh read, so
   * the honest value is "swept just now" and the first real sweep is one
   * full interval away.
   */
  let lastSweepAt = Date.now();
  let stopped = false;
  let ch: RealtimeChannel | null = null;

  const stop = (): void => {
    if (stopped) return;
    stopped = true;
    clearInterval(ticker);
    if (ch) { const c = ch; ch = null; void db().then((sb) => sb.removeChannel(c)); }
  };

  // The self-tuning sweep. Runs often but does real work rarely: at the fast
  // rate until an event has ever arrived, at the slow rate for the ~90s after
  // one has. Also the single owner of the "screen is gone" check.
  const ticker = window.setInterval(() => {
    if (!opts.alive()) { stop(); return; }
    const socketFresh = lastEventAt > 0 && Date.now() - lastEventAt < 90_000;
    const gap = socketFresh ? slow : fast;
    if (Date.now() - lastSweepAt >= gap) {
      lastSweepAt = Date.now();
      void opts.refresh();
    }
  }, 5_000);

  void db().then((sb) => {
    if (stopped) return;
    ch = sb.channel(opts.channel)
      .on(
        // supabase-js types this event union loosely; the shape is correct.
        "postgres_changes" as never,
        { event: opts.event ?? "*", schema: "public", table: opts.table } as never,
        () => {
          lastEventAt = Date.now();
          lastSweepAt = Date.now(); // an event just brought us current
          if (opts.alive()) void opts.refresh(); else stop();
        },
      )
      .subscribe();
  });

  return stop;
}
